import {
  CalendarCheck,
  CircleSlash,
  FileWarning,
  FileUp,
  FileX,
  MailCheck,
  MessageSquare,
  PackageCheck,
  PencilLine,
  RotateCcw,
  Send,
  Undo2,
  type LucideIcon,
} from 'lucide-react';

import { formatarDataHora } from '@/lib/utils';
import type { EventoHistorico, TipoEvento } from '@/features/solicitacoes/tipos';
import { cn } from '@/lib/utils';

const ICONES: Record<TipoEvento, LucideIcon> = {
  CRIACAO: Send,
  MUDANCA_STATUS: MessageSquare,
  LEITURA_REGULACAO: MailCheck,
  REGISTRO_PENDENCIA: FileWarning,
  DEVOLUCAO: Undo2,
  AGENDAMENTO: CalendarCheck,
  DOCUMENTACAO_RECEBIDA: PackageCheck,
  REABERTURA: RotateCcw,
  CANCELAMENTO_SOLICITADO: CircleSlash,
  CANCELAMENTO_CONFIRMADO: CircleSlash,
  CANCELAMENTO_RECUSADO: RotateCcw,
  ANEXO_ADICIONADO: FileUp,
  ANEXO_REMOVIDO: FileX,
  NOTIFICACAO_ENVIADA: MailCheck,
  NOTIFICACAO_REENVIADA: MailCheck,
  COMENTARIO: MessageSquare,
  EDICAO: PencilLine,
};

/** Tom do marcador. Como sempre, acompanha texto — nunca comunica sozinho. */
const TONS: Partial<Record<TipoEvento, string>> = {
  DEVOLUCAO: 'bg-status-devolvido-fundo text-status-devolvido-texto',
  REGISTRO_PENDENCIA: 'bg-status-pendencia-fundo text-status-pendencia-texto',
  AGENDAMENTO: 'bg-status-agendado-fundo text-status-agendado-texto',
  LEITURA_REGULACAO: 'bg-status-lido-fundo text-status-lido-texto',
  CRIACAO: 'bg-status-enviado-fundo text-status-enviado-texto',
  DOCUMENTACAO_RECEBIDA: 'bg-status-processo-fundo text-status-processo-texto',
  CANCELAMENTO_SOLICITADO: 'bg-status-cancelado-fundo text-status-cancelado-texto',
  CANCELAMENTO_CONFIRMADO: 'bg-status-cancelado-fundo text-status-cancelado-texto',
};

/**
 * Linha do tempo da solicitação (seção 14 do plano).
 *
 * Renderizada como lista ordenada: leitores de tela anunciam a sequência e a
 * quantidade de eventos, o que a estrutura visual sozinha não daria.
 */
export function LinhaDoTempo({ eventos }: { eventos: EventoHistorico[] }) {
  if (eventos.length === 0) {
    return <p className="text-silenciado-texto text-sm">Nenhum evento registrado até o momento.</p>;
  }

  return (
    <ol className="flex flex-col">
      {eventos.map((evento, indice) => {
        const Icone = ICONES[evento.tipo] ?? MessageSquare;
        const ultimo = indice === eventos.length - 1;

        return (
          <li key={evento.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full',
                  TONS[evento.tipo] ?? 'bg-silenciado text-silenciado-texto',
                )}
              >
                <Icone className="size-4" aria-hidden="true" />
              </span>
              {!ultimo && <span className="bg-borda w-px flex-1" aria-hidden="true" />}
            </div>

            <div className={cn('min-w-0 flex-1', ultimo ? 'pb-0' : 'pb-5')}>
              <p className="text-sm">{evento.descricao ?? evento.tipo}</p>
              <p className="text-silenciado-texto mt-0.5 text-xs">
                <time dateTime={evento.criadoEm}>{formatarDataHora(evento.criadoEm)}</time>
                {evento.usuario && <> · {evento.usuario.nome}</>}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
